import React, {useEffect, useState, useRef, useLayoutEffect} from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import {logger} from 'packages/common/utils/clientLogger';

// @mui
import {styled} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

// icons
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';

// components
import PhoneInput, {validatePhone} from 'packages/common/components/phone-input';
import {LegalAcceptanceBox} from '../components/LegalAcceptanceBox';
import {NewsletterConsentBox} from 'packages/common/components/newsletter-consent';
import {useEmailValidation} from 'packages/common/hooks/useEmailValidation';
import {EmailSuggestionAlert} from 'packages/common/components/EmailSuggestionAlert';

// constants
import {ERROR_MESSAGES} from '../constants/reservation';
import {clientInputStyles} from '../styles/ReservationFlowStyles';

/**
 * @file Étape de saisie des coordonnées client du flow de réservation.
 * @module packages.common.components.table-reservation.sections.ContactStep
 *
 * Collecte prénom, nom, email, téléphone et acceptation des conditions légales.
 * La validation s'appuie sur un schéma Yup recalculé à chaque changement de langue,
 * avec validation au blur puis revalidation au change pour les champs déjà touchés.
 * Intègre la détection de fautes de frappe de domaine email (useEmailValidation)
 * et pré-remplit les champs depuis `config.userDefaults` quand ils sont disponibles.
 */

// ----------------------------------------------------------------------

const FormField = styled(TextField)(({theme}) => ({
  marginBottom: theme.spacing(2),
  ...clientInputStyles,
}));

// ----------------------------------------------------------------------

/**
 * Étape de saisie et validation des coordonnées du client.
 *
 * @param {Object} props
 * @param {Object} props.formData - Données du formulaire (firstName, lastName, email, phone, termsAccepted)
 * @param {Function} props.updateFormData - Met à jour un champ du formulaire
 * @param {Object} [props.config] - Configuration du restaurant (userDefaults pour pré-remplissage)
 * @param {Function} props.setValidation - Enregistre la fonction de validation auprès du parent
 * @param {Function} props.setErrors - Définit les erreurs de validation
 * @param {Object} [props.errors] - Erreurs de validation courantes, indexées par champ
 * @param {Function} [props.setStepReady] - Active/désactive le bouton suivant selon la validité du formulaire
 * @param {Function} [props.onNextStep] - Passe à l'étape suivante (déclenché par la touche Entrée après validation)
 * @param {Object} [props.companyDetails] - Informations de l'établissement (slug pour les liens légaux)
 * @param {Function} [props.t] - Fonction de traduction générique (fallback sur ERROR_MESSAGES)
 * @return {JSX.Element}
 */
export function ContactStep({
  formData,
  updateFormData,
  config,
  setValidation,
  setErrors,
  errors,
  setStepReady,
  onNextStep,
  companyDetails,
  t,
}) {
  const firstFieldRef = useRef(null);

  const {suggestion: emailSuggestion, domainError: emailDomainError, validateEmail, buildCorrectedEmail, dismiss: dismissEmailWarning} = useEmailValidation();

  // Suivi des champs déjà visités par l'utilisateur : conditionne la revalidation au change
  const [touchedFields, setTouchedFields] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    termsAccepted: false,
  });

  // Auto-focus du premier champ au montage pour améliorer l'accessibilité
  useLayoutEffect(() => {
    const timer = setTimeout(() => {
      firstFieldRef.current?.focus();
    }, 450);
    return () => clearTimeout(timer);
  }, []);

  // Schéma de validation recalculé à chaque changement de langue (messages traduits)
  const validationSchema = React.useMemo(() => yup.object({
    firstName: yup.string().required(
      t ? t('widgets.reservation.validation.firstNameRequired') : ERROR_MESSAGES.FIRST_NAME_REQUIRED,
    ).trim(),
    lastName: yup.string().required(
      t ? t('widgets.reservation.validation.lastNameRequired') : ERROR_MESSAGES.LAST_NAME_REQUIRED,
    ).trim(),
    email: yup.string()
        .required(
          t ? t('widgets.reservation.validation.emailRequired') : ERROR_MESSAGES.EMAIL_REQUIRED,
        )
        .matches(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            t ? t('widgets.reservation.validation.emailInvalid') : ERROR_MESSAGES.EMAIL_INVALID,
        )
        .trim(),
    phone: yup.string()
        .required(
          t ? t('widgets.reservation.validation.phoneRequired') : ERROR_MESSAGES.PHONE_REQUIRED,
        )
        .test('is-valid-phone',
          t ? t('widgets.reservation.validation.phoneInvalid') : ERROR_MESSAGES.PHONE_INVALID,
          (value) => {
            if (!value) return false;
            return validatePhone(value);
          }),
    termsAccepted: yup.boolean()
        .oneOf(
            [true],
            t ? t('widgets.reservation.legal.termsError') : 'Vous devez accepter les conditions générales pour continuer',
        ),
  }), [t]);

  // Pré-remplit les champs vides à partir des valeurs par défaut de la config
  useEffect(() => {
    if (config?.userDefaults) {
      const {firstName, lastName, email, phone} = config.userDefaults;

      if (firstName && !formData.firstName) {
        updateFormData('firstName', firstName);
      }

      if (lastName && !formData.lastName) {
        updateFormData('lastName', lastName);
      }

      if (email && !formData.email) {
        updateFormData('email', email);
      }

      if (phone && !formData.phone) {
        updateFormData('phone', phone);
      }
    }
  }, [config, updateFormData, formData]);

  // Valide un champ isolé via le sous-schéma Yup correspondant
  const validateField = async (field, value) => {
    try {
      await yup.reach(validationSchema, field).validate(value);

      if (errors[field]) {
        const newErrors = {...errors};
        delete newErrors[field];
        setErrors(newErrors);
      }
      return true;
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [field]: err.message,
      }));
      return false;
    }
  };

  // Marque le champ comme touché et le valide lorsque l'utilisateur le quitte
  const handleBlur = (field) => () => {
    setTouchedFields((prev) => ({
      ...prev,
      [field]: true,
    }));

    validateField(field, formData[field] || '');
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    updateFormData(field, value);

    // Revalidation au change uniquement si le champ a déjà été touché
    if (touchedFields[field]) {
      validateField(field, value);
    }
  };

  // Valide tout le formulaire et passe à l'étape suivante sur la touche Entrée
  const handleKeyDown = async (event) => {
    if (event.key === 'Enter' && onNextStep) {
      event.preventDefault();

      const isValid = await validateStep();
      if (isValid) {
        onNextStep();
      }
    }
  };

  // Validation complète du formulaire, agrège toutes les erreurs Yup par champ
  const validateStep = React.useCallback(async () => {
    try {
      await validationSchema.validate(formData, {abortEarly: false});
      return true;
    } catch (err) {
      const yupErrors = {};

      if (err.inner) {
        err.inner.forEach((e) => {
          yupErrors[e.path] = e.message;
        });
      }

      setErrors(yupErrors);
      return false;
    }
  }, [formData, validationSchema, setErrors]);

  useEffect(() => {
    setValidation(() => validateStep);
  }, [setValidation, validateStep]);

  // Active le bouton suivant uniquement quand le formulaire complet est valide
  useEffect(() => {
    if (setStepReady) {
      validationSchema
          .isValid(formData)
          .then((isValid) => {
            setStepReady(isValid);
          })
          .catch((error) => {
            logger.error('ContactStep validation error:', error);
            setStepReady(false);
          });
    }
  }, [formData, validationSchema, setStepReady]);

  return (
    <Fade in timeout={400}>
      <Box sx={{pb: 2}}>
        <Typography variant="h6" sx={{fontWeight: 600, mb: 3}}>
          {t ? t('widgets.reservation.form.yourDetails') : 'Vos coordonnées'}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormField
              fullWidth
              label={t ? t('widgets.reservation.form.firstName') : 'Prénom'}
              value={formData.firstName || ''}
              onChange={handleChange('firstName')}
              onBlur={handleBlur('firstName')}
              onKeyDown={handleKeyDown}
              error={!!errors.firstName}
              helperText={errors.firstName}
              required
              size="medium"
              inputRef={firstFieldRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" color="primary" aria-hidden="true" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormField
              fullWidth
              label={t ? t('widgets.reservation.form.lastName') : 'Nom'}
              value={formData.lastName || ''}
              onChange={handleChange('lastName')}
              onBlur={handleBlur('lastName')}
              onKeyDown={handleKeyDown}
              error={!!errors.lastName}
              helperText={errors.lastName}
              required
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" color="primary" aria-hidden="true" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormField
              fullWidth
              label={t ? t('widgets.reservation.form.email') : 'Email'}
              type="email"
              value={formData.email || ''}
              onChange={handleChange('email')}
              onBlur={(e) => {
                handleBlur('email')(e);
                validateEmail(formData.email);
              }}
              onKeyDown={handleKeyDown}
              error={!!errors.email}
              helperText={errors.email}
              required
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon fontSize="small" color="primary" aria-hidden="true" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <PhoneInput
              value={formData.phone || ''}
              onChange={(value) => {
                updateFormData('phone', value);
                if (touchedFields.phone) {
                  validateField('phone', value);
                }
              }}
              onBlur={handleBlur('phone')}
              error={!!errors.phone}
              helperText={errors.phone}
              label={t ? t('widgets.reservation.form.phone') : 'Téléphone'}
              defaultCountry="BE"
              autoDetectCountry={true}
              // Même marge basse que les autres champs (FormField) : sur mobile, le
              // téléphone est le dernier champ empilé et porte l'écart avec la suite.
              sx={{...clientInputStyles, mb: 2}}
            />
          </Grid>
        </Grid>

        {/* Sous la grille et non dans la colonne de l'email : le bandeau prend toute
            la largeur du formulaire au lieu de s'étirer sur cinq lignes. */}
        <EmailSuggestionAlert
          // Écart du dessus fourni par la marge basse des champs ; même écart sous
          // le bandeau, ajouté à la marge haute de la case newsletter.
          sx={{mb: 1}}
          suggestion={emailSuggestion}
          domainError={emailDomainError}
          email={formData.email}
          onAccept={(correctedDomain) => {
            const corrected = buildCorrectedEmail(formData.email, correctedDomain);
            updateFormData('email', corrected);
            dismissEmailWarning();
            validateField('email', corrected);
          }}
          onDismiss={dismissEmailWarning}
        />

        <NewsletterConsentBox
          checked={formData.subscribeNewsletter !== false}
          onChange={(checked) => updateFormData('subscribeNewsletter', checked)}
          size="small"
          sx={{mt: 1}}
        />

        <LegalAcceptanceBox
          checked={formData.termsAccepted || false}
          onChange={(checked) => {
            updateFormData('termsAccepted', checked);
            setTouchedFields((prev) => ({...prev, termsAccepted: true}));
          }}
          error={touchedFields.termsAccepted ? errors.termsAccepted : undefined}
          t={t}
          slug={companyDetails?.slug}
        />
      </Box>
    </Fade>
  );
}

ContactStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  config: PropTypes.object,
  setValidation: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setStepReady: PropTypes.func,
  onNextStep: PropTypes.func,
  companyDetails: PropTypes.object,
  t: PropTypes.func,
};
